import { makeStyles } from '@material-ui/core';
import { useGetTravelCompensationType } from 'queries';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Arrangement } from '../../types';

import { Grid } from '@mui/material';
import Box from '@mui/material/Box';

import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPage';
import withReadModeField, {
  DefaultReadModeComponent
} from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';

import { convertArrangementTypeToOption } from '../../arrangementsUtils';
import {
  CodeExternField,
  CodeField,
  CompensationTypeIdFieldInner,
  DescriptionField,
  IsExternalField,
  TransportationModeField
} from '../FormFields';
import TravelLinesTable from './TravelLinesTable';
import { TransportationModeEnum } from 'RestAPIs/travel-distances/types';

const useStyles = makeStyles(() => ({
  root: {
    flexDirection: 'column',
    flex: 1
  }
}));

interface Props {
  closeInnerView: () => void;
  rowData?: Arrangement;
}

const CodeFieldWithReadMode = withReadModeField(CodeField);
const CodeExternFieldWithReadMode = withReadModeField(CodeExternField);
const DescriptionFieldWithReadMode = withReadModeField(DescriptionField);
const CompensationTypeIdFieldWithReadMode = withReadModeField(
  CompensationTypeIdFieldInner
);
const TransportationModeFieldWithReadMode = withReadModeField(
  TransportationModeField
);

const renderCompensationTypeIdReadMode = ({
  name,
  label,
  arrangementTypes
}) => {
  const { getValues } = useFormContext();

  const selectedArrangementType = arrangementTypes?.find(
    (arrangementType) => arrangementType.id === getValues(name)
  );

  return (
    <DefaultReadModeComponent
      name={name}
      label={label}
      value={selectedArrangementType?.name}
    />
  );
};

const renderTransportationModeFieldReadMode = ({
  name,
  transportationMode
}) => {
  const { t } = useTranslation('common');
  const { getValues } = useFormContext();

  const selectedTransportationMode = transportationMode?.find(
    (mode) => mode === getValues(name)
  );

  return (
    <DefaultReadModeComponent
      name={t(`travelDistances.transportationModes.${name}`)}
      label={t('travelDistances.transportationMode')}
      value={
        selectedTransportationMode
          ? t(
              `travelDistances.transportationModes.${selectedTransportationMode}`
            )
          : '-'
      }
    />
  );
};

export const ArrangementFormFields: React.FC<Props> = ({
  closeInnerView,
  rowData
}) => {
  const classes = useStyles();
  const { control } = useFormContext();
  const { isEditable } = useEditPageContext();
  const { t } = useTranslation('common');
  const { data: arrangementTypes } = useGetTravelCompensationType();

  return (
    <Box display="flex" className={classes.root}>
      <Box mb={2.5} sx={{ width: '100%' }}>
        <Grid container mb={2.5} rowSpacing={2} columnSpacing={2}>
          <Grid xs={6} item={true}>
            <CodeFieldWithReadMode
              name="code"
              label={t('adminPortal.arrangements.code')}
              control={control}
            />
          </Grid>

          <Grid xs={6} item>
            <CodeExternFieldWithReadMode
              name="codeExtern"
              label={t('adminPortal.arrangements.externalCode')}
              control={control}
            />
          </Grid>

          <Grid xs={6} item>
            <DescriptionFieldWithReadMode
              name="description"
              label={t('adminPortal.arrangements.description')}
              control={control}
              variant="outlined"
            />
          </Grid>

          <Grid xs={6} item>
            <CompensationTypeIdFieldWithReadMode
              name="compensationTypeId"
              label={t('adminPortal.arrangements.typeOfArrangement')}
              control={control}
              options={convertArrangementTypeToOption(arrangementTypes ?? [])}
              readModeRenderer={(props) =>
                renderCompensationTypeIdReadMode({
                  ...props,
                  arrangementTypes: arrangementTypes
                })
              }
            />
          </Grid>

          <Grid xs={6} item>
            <TransportationModeFieldWithReadMode
              name="transportationMode"
              label={''}
              control={control}
              readModeRenderer={(props) =>
                renderTransportationModeFieldReadMode({
                  ...props,
                  transportationMode: Object.values(TransportationModeEnum)
                })
              }
            />
          </Grid>
        </Grid>

        <Box display="flex" gap={2.5}>
          <Grid xs={6} item>
            <IsExternalField control={control} disabled={!isEditable} />
          </Grid>
        </Box>
      </Box>

      <TravelLinesTable rowData={rowData} closeInnerView={closeInnerView} />
    </Box>
  );
};
